/**
 * /agente/habilidades — handbook del agente: qué sabe hacer, en frases que se le
 * dicen, filtrado por rol.
 *
 * Todo el contenido sale de `src/data/agentHandbook.json`, que genera
 * `mcp-server/scripts/generate-agent-handbook.js` desde el registry de tools y
 * `docs/guia-agente/handbook-fuente.json`. Esta pantalla sólo filtra y pinta; no
 * decide permisos. Cada quien ve lo de su rol; el administrador puede mirar cómo
 * se ve para los otros.
 *
 * @module pages/agente/AgenteHandbook
 */

import React, { useMemo, useState } from 'react';
import { Container, Button, Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import handbook from '../../data/agentHandbook.json';
import {
  HANDBOOK_ROLES,
  esRolDelHandbook,
  habilidadesParaRol,
  flujosParaRol,
  conocimientoParaRol,
  novedadesParaRol,
  quienHaceElPaso,
  totalHabilidades,
} from '../../utils/agentHandbookView';
import './agente.css';

const TABS = [
  { id: 'habilidades', label: 'Habilidades' },
  { id: 'flujos', label: 'Flujos' },
  { id: 'conocimiento', label: 'Lo que consulta' },
];

const CopiarFrase = ({ frase }) => {
  const [ok, setOk] = useState(false);
  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(frase);
      setOk(true);
      setTimeout(() => setOk(false), 1800);
    } catch {
      setOk(false);
    }
  };
  return (
    <button type="button" className="agente-copy" onClick={copiar} aria-label={`Copiar la frase: ${frase}`}>
      <i className={`bi ${ok ? 'bi-check2' : 'bi-clipboard'}`} aria-hidden="true"></i>
      <span className="agente-copy-text">{ok ? 'Copiada' : 'Copiar'}</span>
    </button>
  );
};

const AgenteHandbook = () => {
  const { profile } = useAuth();
  const miRol = profile?.role;
  const esAdmin = miRol === 'admin';
  const [rolVisto, setRolVisto] = useState(null);
  const [tab, setTab] = useState('habilidades');
  const [soloProbadas, setSoloProbadas] = useState(false);

  // Sólo el administrador puede mirar como otro rol; los demás ven el suyo.
  const rol = esAdmin && rolVisto ? rolVisto : miRol;
  const rolConocido = esRolDelHandbook(rol);

  const areas = useMemo(
    () => (rolConocido ? habilidadesParaRol(handbook, rol, { soloProbadas }) : []),
    [rol, rolConocido, soloProbadas]
  );
  const flujos = useMemo(() => (rolConocido ? flujosParaRol(handbook, rol) : []), [rol, rolConocido]);
  const conocimiento = useMemo(
    () => (rolConocido ? conocimientoParaRol(handbook, rol) : []),
    [rol, rolConocido]
  );
  const novedades = useMemo(() => (rolConocido ? novedadesParaRol(handbook, rol) : []), [rol, rolConocido]);

  const info = rolConocido ? handbook.roles[rol] : null;

  return (
    <Container className="agente-page py-4" data-testid="agente-handbook">
      <nav aria-label="Ruta" className="agente-crumb">
        <Link to="/agente">← Tu agente</Link>
      </nav>

      <header className="agente-hero">
        <h1 className="agente-title">Qué sabe hacer tu agente</h1>
        <p className="agente-lead">
          Son frases que le puedes decir tal cual, por escrito o por voz. No hay menús ni comandos.
          {rolConocido && info ? ` Esto es lo de tu rol: ${info.etiqueta}.` : ''}
        </p>
      </header>

      {esAdmin && (
        <div className="agente-roles" role="group" aria-label="Ver como otro rol" data-testid="agente-selector-rol">
          <span className="agente-roles-label">Ver como:</span>
          {HANDBOOK_ROLES.map((r) => (
            <button
              key={r}
              type="button"
              className={`agente-chip ${rol === r ? 'is-active' : ''}`}
              aria-pressed={rol === r}
              onClick={() => setRolVisto(r)}
            >
              {handbook.roles[r].etiqueta}
            </button>
          ))}
        </div>
      )}

      {!rolConocido && (
        <div className="modern-card agente-card" data-testid="agente-rol-desconocido">
          <p className="mb-0">
            Todavía no tenemos un catálogo escrito para tu rol. Pregúntale al agente:{' '}
            <strong>«¿qué puedes hacer por mí?»</strong> y te contesta con lo que tu cuenta puede
            hacer hoy.
          </p>
        </div>
      )}

      {rolConocido && info && (
        <>
          <div className="modern-card agente-card agente-rol-card">
            <p className="mb-2">{info.resumen}</p>
            {info.limites.length > 0 && (
              <ul className="agente-limites" data-testid="agente-limites">
                {info.limites.map((l) => (
                  <li key={l}>{l}</li>
                ))}
              </ul>
            )}
          </div>

          {novedades.map((n) => (
            <aside key={n.desde} className="agente-novedad" data-testid="agente-novedad">
              <span className="agente-badge agente-badge--nuevo">Nuevo en v{n.desde}</span>
              <h2 className="agente-card-title">{n.titulo}</h2>
              <p className="agente-frase agente-frase--grande">«{n.frase}»</p>
              <p className="mb-0">{n.detalle}</p>
            </aside>
          ))}

          <div className="agente-tabs" role="tablist" aria-label="Secciones del handbook">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                id={`tab-${t.id}`}
                aria-selected={tab === t.id}
                aria-controls={`panel-${t.id}`}
                className={`agente-tab ${tab === t.id ? 'is-active' : ''}`}
                onClick={() => setTab(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === 'habilidades' && (
            <div role="tabpanel" id="panel-habilidades" aria-labelledby="tab-habilidades">
              <Form.Check
                type="switch"
                id="solo-probadas"
                className="agente-switch"
                label="Sólo las que ya se probaron con el agente"
                checked={soloProbadas}
                onChange={(e) => setSoloProbadas(e.target.checked)}
              />
              <p className="agente-note">
                {totalHabilidades(areas)} frases. La marca <span className="agente-probada">probada</span> quiere
                decir que se ejecutó contra el agente real y salió bien; las demás las soporta el
                sistema pero todavía no se han ejercitado.
              </p>
              {areas.map((area) => (
                <section key={area.id} className="modern-card agente-card agente-area" aria-labelledby={`area-${area.id}`}>
                  <h2 id={`area-${area.id}`} className="agente-card-title">
                    <i className={`bi ${area.icono} me-2`} aria-hidden="true"></i>
                    {area.titulo}
                  </h2>
                  <p className="agente-area-resumen">{area.resumen}</p>
                  <ul className="agente-frases">
                    {area.habilidades.map((h) => (
                      <li key={h.frase}>
                        <span className="agente-frase">«{h.frase}»</span>
                        {h.probada && <span className="agente-probada">probada</span>}
                        <CopiarFrase frase={h.frase} />
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
              {areas.length === 0 && <p>No hay frases con ese filtro.</p>}
            </div>
          )}

          {tab === 'flujos' && (
            <div role="tabpanel" id="panel-flujos" aria-labelledby="tab-flujos">
              <p className="agente-note">
                Cadenas de varios pasos que el agente va completando contigo. Los pasos que no son
                tuyos dicen quién los hace.
              </p>
              {flujos.map((f) => (
                <section key={f.id} className="modern-card agente-card" aria-labelledby={`flujo-${f.id}`}>
                  <h2 id={`flujo-${f.id}`} className="agente-card-title">{f.titulo}</h2>
                  <p className="agente-area-resumen">{f.resumen}</p>
                  <ol className="agente-steps">
                    {f.pasos.map((p, i) => {
                      const mio = p.roles.includes(rol);
                      return (
                        <li key={p.texto} className={mio ? '' : 'is-ajeno'}>
                          <span className="agente-hex">{i + 1}</span>
                          <span>
                            {p.texto}{' '}
                            {!mio && (
                              <em className="agente-quien">Lo hace: {quienHaceElPaso(handbook, p.roles)}.</em>
                            )}
                          </span>
                        </li>
                      );
                    })}
                  </ol>
                </section>
              ))}
            </div>
          )}

          {tab === 'conocimiento' && (
            <div role="tabpanel" id="panel-conocimiento" aria-labelledby="tab-conocimiento">
              <p className="agente-note">
                Lo que el agente mira antes de contestarte, para no responder de memoria.
              </p>
              {conocimiento.map((c) => (
                <section key={c.id} className="modern-card agente-card" aria-labelledby={`con-${c.id}`}>
                  <h2 id={`con-${c.id}`} className="agente-card-title">{c.titulo}</h2>
                  <p className="mb-0">{c.resumen}</p>
                </section>
              ))}
            </div>
          )}
        </>
      )}

      <div className="agente-actions-top">
        <Button as={Link} to="/agente" variant="outline-primary">
          Cómo conectarme al agente
        </Button>
      </div>
    </Container>
  );
};

export default AgenteHandbook;
